<template>
  <div>
    <b-row class="my-2">
      <b-col md="6"><strong>{{totalItems}}</strong> {{ $t($getConst.pages.table.results)}}</b-col>
      <b-col md="6">
        <b-form inline class="justify-content-sm-end">
          <label class="mr-sm-2">{{ $t($getConst.pages.table.items_per_page) | capitalize}}:</label>
          <b-form-select
            class="mb-2 mr-sm-2 mb-sm-0 form-control"
            :disabled="isLoading"
            v-model="perPage"
            :options="[{ text: 'Choose...', value: null }, 5, 10, 20, 50]"
            :value="null"
          ></b-form-select>
        </b-form>
      </b-col>
    </b-row>
    <b-table
      id="data-table"
      :table-class="tableClass"
      :thead-class="theadClass"
      :fields="fields"
      :items="items"
      :hover="hover"
      :striped="striped"
      :outlined="outlined"
      :borderless="borderless"
      :small="small"
      :fixed="fixed"
      :responsive="responsive"
      :no-local-sorting="true"
      :busy="isLoading"
      :per-page="0"
      show-empty
      @sort-changed="onSortChanged">

      <!--   Header Row   -->
      <template v-for="field in fields" v-slot:[`head(${field.key})`]="data">
        <span v-if="data.field.label">
          {{ $t(data.field.label.toLowerCase()) | capitalize }}
        </span>
        <span v-else>
          {{ $t(data.field.key.toLowerCase()) | capitalize }}
        </span>
      </template>

      <!--   Filter Row   -->
      <template v-if="hasFilterableColumn" v-slot:top-row>
        <template v-for="field in fields">
          <TableFilterColumn
            :field="field"
            :filters="filters"
            :is-loading="isLoading"
            @onFilter="addFilter($event, field.key)"
          />
        </template>
      </template>

      <template v-for="field in fields" v-slot:[`cell(${field.key})`]="data">
        <!-- Checkbox cell -->
        <template v-if="field.key === 'checkbox'">
          <b-form-checkbox v-model="data.item.checkbox" @change="setCheckedIds(data.item.id)"></b-form-checkbox>
        </template>

        <!--   Nested Attribute Cell   -->
        <template v-if="field.key.indexOf('.') > -1">
          <ul v-if="Array.isArray(data.item[field.key.split('.')[0]])">
            <li v-for="item in data.item[field.key.split('.')[0]]">
              {{item[field.key.split('.')[1]]}}
            </li>
          </ul>
          <span v-else>{{ data.item[field.key.split('.')[0]][field.key.split('.')[1]] }}</span>
        </template>

        <!--   Action Cell   -->
        <template v-else-if="field.key === 'action'">
          <template v-for="action in field.actions">
            <slot v-bind:action="action" v-bind:user="data.item" :name="'button-'+action.action">
              <b-button
                v-if="(!action.roles && !action.permissions) ||
                  (action.roles && action.roles.length && hasRole(action.roles)) ||
                  (action.permissions && action.permissions.length && hasAnyOfPermissions(action.permissions))"
                :variant="action.variant"
                class="ml-2 border-0"
                size="sm"
                @click="onAction(action.action, data.item)">
                <i :class="action.icon"></i>
              </b-button>
            </slot>
          </template>
        </template>

        <!--   Default Cell   -->
        <template v-else>
          <template v-if="field.key !== 'checkbox'">
            <slot :name="field.key" v-bind:data="data.item[field.key]" v-bind:row="data.item">{{data.item[field.key]}}</slot>
          </template>
        </template>
      </template>

      <!--   message when table is empty   -->
      <template #empty="scope">
        {{$t($getConst.pages.table.no_data_to_show) | capitalize}}
      </template>

    </b-table>

    <b-pagination
      v-on:change="getData($event)"
      :disabled="isLoading"
      :total-rows="totalItems"
      :per-page="perPage"
      v-model="currentPage"
      :current-page="currentPage"/>
  </div>
</template>
<script>
    import httpClient from "../../../api/_httpClient";
    import TableFilterColumn from "./_tabelFilterColumn";
    import CONSTANT from "../../../shared/locales/en.json";
    import AppSpinner from "../utils/AppSpinner";

    export default {
      name: "AppDataTable",
      components: {AppSpinner, TableFilterColumn},
      emits:['getCheckedIds'],
      props: {
        fields: null,
        actions: null,
        endPoint: {
          type: String
        },

        hover: {
          type: Boolean,
          default: true
        },
        striped: {
          type: Boolean,
          default: false
        },
        outlined: {
          type: Boolean,
          default: false
        },
        borderless: {
          type: Boolean,
          default: false
        },
        small: {
          type: Boolean,
          default: false
        },
        fixed: {
          type: Boolean,
          default: false
        },
        responsive: {
          default: true
        },
        tableClass: {
          type: String,
          default: 'text-center'
        },

        theadClass: {
          type: String,
          default: 'text-center'
        },

        sortBy: {
          type: String,
          default: null
        },
        sortDesc: {
          type: Boolean,
          default: false
        },
        selectable: {
          type: Boolean,
          default: false
        },
      },
      data() {
        return {
          constants: CONSTANT,
          items: [],
          isLoading: false,
          nbPage: 1,
          currentPage: 1,
          totalItems: 0,
          perPage: 5,
          filters: [],

          showFilter: false,
          checkedIds: []
        }
      },
      created() {
        this.getData();
        this.displayActions();
      },
      watch: {
        perPage() {
          this.getData();
        },
        endPoint() {
          this.getData();
        }
      },
      methods: {
        getData(nbPage = this.nbPage, perPage = this.perPage, sortBy = this.sortBy, sortDesc = this.sortDesc) {
          this.isLoading = true;
          this.getApiCall(nbPage, perPage, sortBy, sortDesc, this.filters).then(
            response => {
              this.items = response.data.data
              this.currentPage = response.data['current_page']
              this.totalItems = response.data['total']
              this.isLoading = false;
            }
          ).catch(
            () => {
              this.$snotify.error(this.$t(this.$getConst.pages.alerts.unknown_error));
              this.isLoading = false;
            }
          )
        },
        onSortChanged(ctx) {
          this.sortBy = ctx.sortBy;
          this.sortDesc = ctx.sortDesc;
          this.getData();
        },
        getApiCall(page = 1, perPage, sortBy = false, sortDesc = false, filters = []) {
          let params = '?page=' + page + '&per_page=' + perPage
          if (sortBy) {
            let order = 'asc';
            if (sortDesc === true) {
              order = 'desc';
            }
            params += '&sort_by=' + sortBy + '&order=' + order
          }
          for (let item in filters) {
            if (filters[item]) {
              params += '&' + item + '=' + filters[item]
            }
          }
          return httpClient.get(this.endPoint + params)
        },
        checkActions() {
          let allActions = [];
          this.actions.forEach(action => {
            let added = false;
            if (!action.permissions && !action.roles) {
              allActions.push(action);
            } else {
              if (action.permissions && this.hasAnyOfPermissions(action.permissions)) {
                allActions.push(action);
                added = true;
              }
              if (!added && action.roles && this.hasRole(action.roles)) {
                allActions.push(action);
              }
            }
          })

          return allActions;
        },
        displayActions() {
          if (this.actions && this.checkActions().length !== 0) {
            this.fields.push({ key: 'action', label: this.$t('action'), actions: this.checkActions() })
          }
        },
        onAction(action, id) {
          this.$emit(action, id);
        },
        removeItem(item) {
          this.items.splice(this.items.indexOf(item), 1);
          this.totalItems--;
          if (this.items.length === 0) {
            this.getData();
          }
        },
        editItem(item) {
          const itemToUpdate = this.items.findIndex(element => element.id === item.id);
          this.items[itemToUpdate] = item
          this.$root.$emit('bv::refresh::table', 'data-table')
        },
        addItem(item) {
          if (this.perPage === this.items.length) {
            this.items.unshift(item);
            this.items.pop();
          } else {
            this.items.push(item);
          }

          this.totalItems++;
        },
        addFilter(input, field) {
          this.filters[field] = input && input.value ? input.value : input;
          this.getData();
        },
        hasFilterableColumn() {
          let hasFilterable = false;
          this.fields.forEach(field => {
            if (field.filterable) {
              hasFilterable = true;
              return;
            }
          });
          return hasFilterable;
        },
        setCheckedIds(id) {
          !this.checkedIds.includes(id) ? this.checkedIds.push(id) : this.checkedIds = this.checkedIds.filter(item => item !== id);
          this.$emit('getCheckedIds', this.checkedIds);
        }
      },
      mounted() {
        if (this.selectable) {
          this.fields.unshift({key: 'checkbox', label: ' ', sortable: false, filterable: false});
        }
      }
    }
</script>

<style scoped>

</style>
